import { useState } from 'react';
import { Lock, Globe, Palette, Check, Eye, EyeOff } from 'lucide-react';
import { Card } from './core/Card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AppsView } from './AppsView';
import { useUpdatePassword } from '../graphql/hooks/useUserDashboard';

export function UserSettingsView() {
  // Security section state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const { mutate: updatePasswordMutation } = useUpdatePassword();

  // Notifications state
  // const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  // const [requestApprovedNotif, setRequestApprovedNotif] = useState(true);
  // const [requestRejectedNotif, setRequestRejectedNotif] = useState(true);
  // const [notificationSuccess, setNotificationSuccess] = useState(false);

  // Preferences state
  const [language, setLanguage] = useState('de');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [preferencesSuccess, setPreferencesSuccess] = useState(false);

  // Password validation
  const validatePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Bitte füllen Sie alle Felder aus');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein');
      return false;
    }

    return true;
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (!validatePassword()) return;

    updatePasswordMutation(
      {
        old_password: currentPassword,
        new_password: newPassword,
      },
      {
        onSuccess: (data) => {
          setPasswordSuccess(true);

          // Optional: if backend returns new token
          if (data?.token) {
            localStorage.setItem('token', data.token);
          }

          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');

          setTimeout(() => setPasswordSuccess(false), 3000);
        },
        onError: (error: any) => {
          setPasswordError(
            error?.response?.errors?.[0]?.message ||
              'Passwort konnte nicht geändert werden'
          );
        },
      }
    );
  };

  // const handleNotificationSave = () => {
  //   setNotificationSuccess(true);
  //   setTimeout(() => setNotificationSuccess(false), 3000);
  // };

  const handlePreferencesSave = () => {
    setPreferencesSuccess(true);
    setTimeout(() => setPreferencesSuccess(false), 3000);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-[20px] font-medium text-[#1c1c1c] mb-2">
          Einstellungen
        </h2>
        <p className="text-[#6b6b6b] text-[14px]">
          Verwalten Sie Ihre persönlichen Einstellungen und Präferenzen
        </p>
      </div>

      {/* Security Section */}
      <Card>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-[#e9e9e9]">
            <div className="size-10 rounded-[10px] bg-[#0084c7]/10 flex items-center justify-center">
              <Lock className="size-5 text-[#0084c7]" />
            </div>
            <div>
              <h3 className="text-[18px] font-medium text-[#1c1c1c]">
                Sicherheit
              </h3>
              <p className="text-[#6b6b6b] text-[14px]">
                Passwort ändern und Sicherheitseinstellungen verwalten
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="current-password">Aktuelles Passwort</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Geben Sie Ihr aktuelles Passwort ein"
                  className="pr-10 box-border"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b6b6b] hover:text-[#1c1c1c] transition-colors"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  className="pr-10 box-border"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b6b6b] hover:text-[#1c1c1c] transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Passwort bestätigen</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Neues Passwort wiederholen"
                  className="pr-10 box-border"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b6b6b] hover:text-[#1c1c1c] transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {passwordError && (
              <div className="p-3 bg-[#dc2626]/10 border border-[#dc2626]/20 rounded-[10px] flex items-start gap-2">
                <div className="size-5 rounded-full bg-[#dc2626] text-white flex items-center justify-center mt-0.5 shrink-0">
                  <span className="text-[12px]">!</span>
                </div>
                <p className="text-[#dc2626] text-[14px]">{passwordError}</p>
              </div>
            )}

            {/* Success Message */}
            {passwordSuccess && (
              <div className="p-3 bg-[#16a34a]/10 border border-[#16a34a]/20 rounded-[10px] flex items-center gap-2">
                <Check className="size-5 text-[#16a34a]" />
                <p className="text-[#16a34a] text-[14px] font-medium">
                  Passwort erfolgreich geändert
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <Button type="submit" className="rounded-[10px]">
                Passwort ändern
              </Button>
            </div>
          </form>
        </div>
      </Card>

      {/* Notifications Section */}
      {/* <Card>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-[#e9e9e9]">
            <div className="size-10 rounded-[10px] bg-[#0084c7]/10 flex items-center justify-center">
              <Bell className="size-5 text-[#0084c7]" />
            </div>
            <div>
              <h3 className="text-[18px] font-medium text-[#1c1c1c]">
                Benachrichtigungen
              </h3>
              <p className="text-[#6b6b6b] text-[14px]">
                Steuern Sie, welche Benachrichtigungen Sie erhalten möchten
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-[10px]">
              <div>
                <p className="text-[#1c1c1c] font-medium">
                  Benachrichtigungen aktivieren
                </p>
                <p className="text-[#6b6b6b] text-[14px]">
                  Alle Benachrichtigungen ein- oder ausschalten
                </p>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={setNotificationsEnabled}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b border-[#e9e9e9]">
                <div>
                  <p className="text-[#1c1c1c]">Antrag genehmigt</p>
                  <p className="text-[#6b6b6b] text-[13px]">
                    Benachrichtigung bei Genehmigung eines Antrags
                  </p>
                </div>
                <Switch
                  checked={requestApprovedNotif}
                  onCheckedChange={setRequestApprovedNotif}
                  disabled={!notificationsEnabled}
                />
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-[#1c1c1c]">Antrag abgelehnt</p>
                  <p className="text-[#6b6b6b] text-[13px]">
                    Benachrichtigung bei Ablehnung eines Antrags
                  </p>
                </div>
                <Switch
                  checked={requestRejectedNotif}
                  onCheckedChange={setRequestRejectedNotif}
                  disabled={!notificationsEnabled}
                />
              </div>
            </div>

            {notificationSuccess && (
              <div className="p-3 bg-[#16a34a]/10 border border-[#16a34a]/20 rounded-[10px] flex items-center gap-2">
                <Check className="size-5 text-[#16a34a]" />
                <p className="text-[#16a34a] text-[14px] font-medium">
                  Benachrichtigungseinstellungen gespeichert
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleNotificationSave}
                className="rounded-[10px]"
              >
                Einstellungen speichern
              </Button>
            </div>
          </div>
        </div>
      </Card> */}

      {/* Mobile Apps */}
      <div className="pt-2">
        <AppsView />
      </div>

      {/* Preferences Section */}
      <Card>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-[#e9e9e9]">
            <div className="size-10 rounded-[10px] bg-[#0084c7]/10 flex items-center justify-center">
              <Palette className="size-5 text-[#0084c7]" />
            </div>
            <div>
              <h3 className="text-[18px] font-medium text-[#1c1c1c]">
                Präferenzen
              </h3>
              <p className="text-[#6b6b6b] text-[14px]">
                Passen Sie das Erscheinungsbild der Anwendung an
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Language Selector */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="size-4 text-[#6b6b6b]" />
                <Label htmlFor="language">Sprache</Label>
              </div>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full form-select h-[44px] px-4 bg-white border border-[#e9e9e9] rounded-[10px] text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
              >
                <option value="de">Deutsch</option>
              </select>
            </div>

            {/* Theme Toggle */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Palette className="size-4 text-[#6b6b6b]" />
                <Label>Design</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setTheme('light')}
                  className={`p-4 border-2 rounded-[10px] transition-all ${
                    theme === 'light'
                      ? 'border-[#0084c7] bg-[#0084c7]/5'
                      : 'border-[#e9e9e9] hover:border-[#c7c7c7]'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="h-12 bg-white border border-[#e9e9e9] rounded-[6px]" />
                    <p className="text-[14px] font-medium text-[#1c1c1c]">Hell</p>
                  </div>
                  {theme === 'light' && (
                    <div className="mt-2 flex items-center gap-1 text-[#0084c7] text-[13px]">
                      <Check className="size-3" />
                      <span>Aktiv</span>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setTheme('dark')}
                  className={`p-4 border-2 rounded-[10px] transition-all ${
                    theme === 'dark'
                      ? 'border-[#0084c7] bg-[#0084c7]/5'
                      : 'border-[#e9e9e9] hover:border-[#c7c7c7]'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="h-12 bg-[#1c1c1c] border border-[#3a3a3a] rounded-[6px]" />
                    <p className="text-[14px] font-medium text-[#1c1c1c]">Dunkel</p>
                  </div>
                  {theme === 'dark' && (
                    <div className="mt-2 flex items-center gap-1 text-[#0084c7] text-[13px]">
                      <Check className="size-3" />
                      <span>Aktiv</span>
                    </div>
                  )}
                </button>
              </div>
              <p className="text-[#6b6b6b] text-[13px] italic">
                Hinweis: Dunkles Design wird in einer zukünftigen Version verfügbar sein
              </p>
            </div>

            {/* Success Message */}
            {preferencesSuccess && (
              <div className="p-3 bg-[#16a34a]/10 border border-[#16a34a]/20 rounded-[10px] flex items-center gap-2">
                <Check className="size-5 text-[#16a34a]" />
                <p className="text-[#16a34a] text-[14px] font-medium">
                  Präferenzen gespeichert
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handlePreferencesSave}
                className="rounded-[10px]"
              >
                Einstellungen speichern
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
